import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import { IconButton } from "@mui/material";
import moment from "moment";
import ChatBox from "./ChatBox";
import Message from "./Message";
import "./Personal.css";
import AgentList from "../../Components/AgentList";
import { sendInternalMessage } from "../../Store/Actions/ChatAction";
import { SET_SELECTED_AGENT_ID } from "../../Store/Types/AgentTypes";

const Personal = ({ activeAgents, isAgentListLoading, setIsTabsShown }) => {
  const chatStorage = JSON.parse(sessionStorage.getItem("chatInternal"));
  const [searchValue, setSearchValue] = useState("");
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatList, setChatList] = useState([]);
  const [messageToSend, setMessageToSend] = useState("");
  const dispatch = useDispatch();
  const { myInfo } = useSelector((state) => state.auth);

  // Update chatList state when chat storage is updated
  useEffect(() => {
    if (sessionStorage.getItem("chatInternal")) {
      setChatList(JSON.parse(sessionStorage.getItem("chatInternal")));
    }

    const handler = () => {
      setChatList(JSON.parse(sessionStorage.getItem("chatInternal")));
    };

    window.addEventListener("updateChatInternal", handler);

    return () => {
      window.removeEventListener("updateChatInternal", handler);
    };
  }, []);

  // Used to re-render message list when new chat is received
  useEffect(() => {
    // const chatStorage = JSON.parse(sessionStorage.getItem("chatInternal"));

    if (chatStorage && selectedChat) {
      setSelectedChat(
        chatStorage[
          chatStorage.findIndex((chat) => chat.id === selectedChat?.id)
        ]
      );
    }
  }, [chatList]);

  const handleSendMessage = () => {
    const payload = {
      id: Math.random().toString(16).slice(2),
      senderID: myInfo.user.AgentID,
      senderName: myInfo.user.AgentName,
      message: messageToSend,
      time: moment().toISOString(),
      recipientID: selectedChat.id,
    };

    sendInternalMessage(payload)
      .then((response) => {
        if (response.status) {
          // Get current chat storage
          // const chatStorage = JSON.parse(
          //   sessionStorage.getItem("chatInternal")
          // );

          // Check if chat already existed
          let index = chatStorage.findIndex(
            (chat) => chat.id === selectedChat.id
          );

          const newMessage = {
            id: response.data.id,
            sender: myInfo.user.AgentName,
            message: response.data.message,
            time: response.data.time,
            isRead: false,
          };

          // If chat already existed, push the new message
          if (index || index === 0) {
            chatStorage[index].messages.push(newMessage);
            // Else, create a new chat
          } else {
            const selectedAgent = activeAgents.find(
              (agent) => agent.AgentID === selectedChat.id
            );

            chatStorage.push({
              id: selectedChat.id,
              agentName: `${selectedAgent.FirstName} ${selectedAgent.LastName}`,
              time: response.data.time,
              messages: [newMessage],
            });
          }

          // Update storage
          sessionStorage.setItem("chatInternal", JSON.stringify(chatStorage));

          // Used to re-render the message list
          setSelectedChat(
            chatStorage[
              chatStorage.findIndex((chat) => chat.id === selectedChat.id)
            ]
          );
        } else {
          console.log("Error send message", response.error);
          toast.error("Error send message");
        }
      })
      .catch((err) => {
        console.log("Error send message", err);
        toast.error("Error send message");
      });

    // Reset chat box
    setMessageToSend("");
  };

  return (
    <section className="personal">
      {selectedChat ? (
        <section className="chat-room">
          <header>
            <IconButton
              size="small"
              onClick={() => {
                setSelectedChat(null);

                dispatch({
                  type: SET_SELECTED_AGENT_ID,
                  payload: null,
                });

                setIsTabsShown(true);
                setChatList(JSON.parse(sessionStorage.getItem("chatInternal")));
              }}
            >
              <Icon
                height="26"
                icon="material-symbols:arrow-back-ios-new-rounded"
              />
            </IconButton>

            <p className="agent-name">{selectedChat.agentName}</p>
          </header>

          <div className="message-list">
            {/* Reverse list because column-reverse flex direction is used */}
            {selectedChat.messages?.toReversed().map((message, index) => (
              <Message
                key={index}
                message={message}
                recipientID={selectedChat.id}
              />
            ))}
          </div>

          <ChatBox
            placeholder="Type a message..."
            message={messageToSend}
            setMessage={setMessageToSend}
            handleSendMessage={handleSendMessage}
          />
        </section>
      ) : (
        <section className="main">
          <div className="agent-search">
            <div className="search-container">
              <SearchOutlined className="icon" />

              <input
                type="search"
                className="search"
                placeholder="Search agent"
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value.toLowerCase());
                }}
              />
            </div>
          </div>

          <AgentList
            // Filter agents according to search value
            activeAgents={activeAgents.filter((agent) => {
              const fullName = `${agent.FirstName} ${agent.LastName}`;
              return fullName.toLowerCase().includes(searchValue);
            })}
            isLoading={isAgentListLoading}
            setSelectedChat={setSelectedChat}
            setIsTabsShown={setIsTabsShown}
          />

          <div className="chat-list">
            {chatList?.map((chat, index) => {
              const lastMessage = chat.messages.at(-1);

              if (lastMessage) {
                return (
                  <div
                    className="chat-item"
                    key={index}
                    onClick={() => {
                      setSelectedChat(chat);
                      setIsTabsShown(false);

                      // const chatStorage = JSON.parse(
                      //   sessionStorage.getItem("chatInternal")
                      // );

                      const index = chatStorage.findIndex(
                        (storedChat) => storedChat.id === chat.id
                      );

                      // Set selected message as viewed
                      chatStorage[index].isViewed = true;

                      // Update storage
                      sessionStorage.setItem(
                        "chatInternal",
                        JSON.stringify(chatStorage)
                      );

                      // Dispatch event, used to trigger badge visibility useEffect function in AgentStatus
                      window.dispatchEvent(new Event("updateChatInternal"));
                    }}
                  >
                    <div className="info">
                      <p className="agent-name">{chat.agentName}</p>

                      <p className="time">
                        {moment(lastMessage.time).format("HH:mm")}
                      </p>
                    </div>

                    <div className="last-message">
                      <p>
                        {`${
                          lastMessage.sender === myInfo.user.AgentName
                            ? "You"
                            : lastMessage.sender
                        }: ${lastMessage.message}`}
                      </p>

                      {lastMessage.isBroadcast ? (
                        <Icon
                          color="#727272"
                          height="26"
                          icon="fluent:megaphone-loud-24-filled"
                        />
                      ) : (
                        lastMessage.sender === myInfo.user.AgentName && (
                          <Icon
                            color={lastMessage.isRead ? "#0343C7" : "#727272"}
                            height="26"
                            icon="bi:check-all"
                          />
                        )
                      )}
                    </div>
                  </div>
                );
              } else {
                return null;
              }
            })}
          </div>
        </section>
      )}
    </section>
  );
};

export default Personal;
